/**
 * The overview.
 *
 * What an operator wants on opening the tab: is anything wrong, and what needs
 * doing. Counts alone would be decoration — the two that matter are devices not
 * reporting and devices nobody has finished installing, because both are work.
 */

import { deviceListViewModel, type DeviceSummary, type FetchState } from './deviceViewModel.ts';
import { routeHref } from './routing.ts';

interface Props {
  devices: DeviceSummary[];
  fetchState: FetchState;
  now: number;
  clientCount: number;
  siteCount: number;
}

export function Overview({ devices, fetchState, now, clientCount, siteCount }: Props): React.JSX.Element {
  const model = deviceListViewModel(devices, fetchState, now);

  const offline = model.devices.filter((d) => d.health === 'offline');
  const awaitingClaim = model.devices.filter((d) => d.claimState === 'unclaimed');
  const cameras = devices.reduce((total, device) => total + device.cameras.length, 0);
  const streaming = devices.reduce(
    (total, device) => total + device.cameras.filter((c) => c.state === 'streaming').length,
    0,
  );

  return (
    <>
      {model.connectionNotice ? (
        <p className="notice notice--warn" role="status" style={{ marginBottom: 'var(--s5)' }}>
          {model.connectionNotice}
        </p>
      ) : null}

      <div className="metrics">
        <div className="metric">
          <p className="metric__label">Devices online</p>
          <p className="metric__value">
            {model.onlineCount}
            <span style={{ color: 'var(--fg-subtle)', fontSize: '1rem' }}> / {model.devices.length}</span>
          </p>
        </div>

        <div className={offline.length > 0 ? 'metric metric--attention' : 'metric'}>
          <p className="metric__label">Not reporting</p>
          <p className="metric__value">{offline.length}</p>
          <p className="metric__hint">
            {offline.length === 0 ? 'Everything is checking in' : 'Devices past their heartbeat window'}
          </p>
        </div>

        <div className={awaitingClaim.length > 0 ? 'metric metric--attention' : 'metric'}>
          <p className="metric__label">Awaiting claim</p>
          <p className="metric__value">{awaitingClaim.length}</p>
          <p className="metric__hint">
            {awaitingClaim.length === 0 ? 'All devices commissioned' : 'Provisioned but never claimed'}
          </p>
        </div>

        <div className="metric">
          <p className="metric__label">Cameras streaming</p>
          <p className="metric__value">
            {streaming}
            <span style={{ color: 'var(--fg-subtle)', fontSize: '1rem' }}> / {cameras}</span>
          </p>
        </div>

        <div className="metric">
          <p className="metric__label">Clients</p>
          <p className="metric__value">{clientCount}</p>
          <p className="metric__hint">{siteCount} site{siteCount === 1 ? '' : 's'}</p>
        </div>
      </div>

      {/* Only what needs attention, and only when there is any. A permanent
          "nothing is wrong" panel trains people to stop reading it. */}
      {offline.length > 0 || awaitingClaim.length > 0 ? (
        <section className="section">
          <div className="section__head">
            <h2>Needs attention</h2>
            <a className="link" href={routeHref('devices')}>
              All devices
            </a>
          </div>

          <div className="card">
            <table className="table">
              <thead>
                <tr>
                  <th>Device</th>
                  <th>Site</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {[...awaitingClaim, ...offline.filter((d) => d.claimState === 'claimed')].map((device) => (
                  <tr key={device.id}>
                    <td>{device.name}</td>
                    <td style={{ color: 'var(--fg-muted)' }}>{device.siteName ?? '—'}</td>
                    <td>
                      {device.claimState === 'unclaimed' ? (
                        <span className="pill">Awaiting claim</span>
                      ) : (
                        <span className="status status--offline">
                          <span className="status__dot" />
                          Last seen {device.lastSeenLabel}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : model.devices.length > 0 ? (
        <div className="empty">
          <p className="empty__title">Everything looks healthy</p>
          <p>All devices are reporting and commissioned.</p>
        </div>
      ) : (
        <div className="empty">
          <p className="empty__title">No devices yet</p>
          <p>Add a client, create a site under it, then provision your first device.</p>
        </div>
      )}
    </>
  );
}
